import { Component, OnInit, ViewChild } from '@angular/core';
import { ViewportScroller } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, FilterService, MenuItem, MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { Lang } from 'src/app/domain/models/Language';
import { BasicUser } from 'src/app/domain/models/User';
import { ErrorMessageUtil } from 'src/app/ui/util/error-message-util';
import { Country } from 'src/app/domain/models/Country';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { NewPort, Port } from 'src/app/domain/models/Port';
import { Airport, NewAirport } from 'src/app/domain/models/Airport';
import { PortGateway } from 'src/app/application/gateway/PortGateway';
import { AirportGateway } from 'src/app/application/gateway/AirportGateway';
import { Table } from 'primeng/table';

@Component({
  selector: 'app-admin-manage-ports-airports',
  templateUrl: './admin-manage-ports-airports.component.html',
  styleUrls: ['./admin-manage-ports-airports.component.css'],
  providers: [ConfirmationService]
})

export class AdminManagePortsAirportsComponent implements OnInit {

  @ViewChild('tablePorts') tablePorts!: Table;
  @ViewChild('tableAirports') tableAirports!: Table;

  breadcrumbHome: MenuItem = {}
  countries: Country[] = []
  ports: Port[] = []
  airports: Airport[] = []

  selectedType: string = 'port';

  typeOptions = [
    { label: 'Puerto', value: 'port' },
    { label: 'Aeropuerto', value: 'airport' }
  ];

  loading: boolean = false
  submit: boolean = false
  profile: BasicUser | undefined
  langs: Lang[] = []
  lang: Lang | undefined

  totalCountPorts = 0;
  pageSizePorts = 5;
  lastPage?: number;
  lastSize?: number;

  totalCountAirports = 0;
  pageSizeAirports = 5;

  formGroup: FormGroup = this._formBuilder.group({
    name: ['', Validators.required],
    countryId: [null, Validators.required],
    locode: ['', Validators.required],
    latitude: [null],
    longitude: [null]
  })

  isSectionLoaded: boolean = false;
  fragmentToScroll: string | null = null;

  constructor(private _router: Router,
    private _formBuilder: FormBuilder,
    private _authService: AuthGateway,
    private _messageService: MessageService,
    public translate: TranslateService,
    private _countryService: CountryGateway,
    private _portService: PortGateway,
    private _airportService: AirportGateway
  ){
  }

  ngOnInit(): void {
    this.profile = this._authService.getUser()
    if (this.profile) {
      this.translate.use(this.profile.lang)
    }
    this.loadBreadcrumb();

    this.getCountries();
  }

  getPortsPaged(event: any)
  {
      const pageNumber = Math.floor(event.first / event.rows) + 1;
      const pageSize = event.rows;

      this.getPorts(pageSize, pageNumber);
  }

  getAirportsPaged(event: any)
  {
      const pageNumber = Math.floor(event.first / event.rows) + 1;
      const pageSize = event.rows;

      this.getAirports(pageSize, pageNumber);
  }

  getPorts(pageSize: number = this.pageSizePorts, pageNumber: number = 1)
  {
    this.loading = true;
    const profile = this._authService.getUser();

    this._portService.getAllPaged(pageNumber, pageSize).subscribe({
      next: (v) => {
        this.ports = v.items

        this.totalCountPorts = v.totalCount;

        this.loading = false;
      },
      error: (e) => {
        console.log(e)
        this.loading = false
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Port.ManyRetrieveError') })
      }
    })
  }

  refreshPorts() {
  const event = this.tablePorts.createLazyLoadMetadata();
  this.getPortsPaged(event);
  }

  getAirports(pageSize: number = this.pageSizeAirports, pageNumber: number = 1)
  {
    this.loading = true;

    this._airportService.getAllPaged(pageNumber, pageSize).subscribe({
      next: (v) => {
        this.airports = v.items

        this.totalCountAirports = v.totalCount;

        this.loading = false;
      },
      error: (e) => {
        console.log(e)
        this.loading = false
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Airport.ManyRetrieveError') })
      }
    })
  }

  refreshAirports() {
  const event = this.tableAirports.createLazyLoadMetadata();
  this.getAirportsPaged(event);
  }

  getCountries() {
    this._countryService.getAll().subscribe({
      next: (v) => {
        this.countries = v
      },
      error: (e) => {
        this._messageService.add({ severity: 'error',
          summary: this.translate.instant('MessageService.Summary.FailedAction'),
          detail: this.translate.instant('MessageService.Country.ManyRetrieveError') });
        console.log(e)
      },
      complete: () => console.info('Country query successfully')
    })
  }

  submitForm() {
    this.loading = true
    if (this.formGroup.valid) {
      let request = null;

      if(this.selectedType == 'port'){
        const newPort: NewPort = {
          name: this.formGroup.controls["name"].value,
          countryId: this.formGroup.controls["countryId"].value,
          locode: this.formGroup.controls["locode"].value,
          latitude: this.formGroup.controls["latitude"].value,
          longitude: this.formGroup.controls["longitude"].value
        }

       this._portService.create(newPort).subscribe({
        next: () => {
          this.formGroup.reset()
          this.submit = false
          this.loading = false
          this.refreshPorts();
          this.refreshAirports();
          this._messageService.add({ severity: 'success',
            summary: this.translate.instant('MessageService.Summary.SuccessAction'),
            detail: this.translate.instant('MessageService.Port.CreateSuccess') })
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Port.CreateError') });
        }
      })

      }else{
        const newAirport: NewAirport = {
          name: this.formGroup.controls["name"].value,
          countryId: this.formGroup.controls["countryId"].value,
          locode: this.formGroup.controls["locode"].value
        }

        this._airportService.create(newAirport).subscribe({
        next: () => {
          this.formGroup.reset()
          this.submit = false
          this.loading = false
          this._messageService.add({ severity: 'success',
            summary: this.translate.instant('MessageService.Summary.SuccessAction'),
            detail: this.translate.instant('MessageService.Airport.CreateSuccess') })
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error',
            summary: this.translate.instant('MessageService.Summary.FailedAction'),
            detail: this.translate.instant('MessageService.Airport.CreateError') });
        }
      })

      }


    }
    else {
      this.loading = false
    }
  }

    onTypeChange() {

      if (this.selectedType === 'airport') {

        this.formGroup.patchValue({
          latitude: null,
          longitude: null
        });

      }
    }

    loadBreadcrumb() {
      this.translate.get([
        'PortAirportPage.PageHomeTitle',
      ]).subscribe({
        next: (tranlations) => {
          this.breadcrumbHome = {
            label: tranlations['PortAirportPage.PageHomeTitle']
          }
        },
        error: () => {}
      })
    }

    getErrorMessage(fieldName: string): string {
      return ErrorMessageUtil.getErrorMessage(this.formGroup, this.translate, fieldName)
    }

    get placeholder(): string {
      return this.translate.instant('SwitchLangComponent.SwitchLangComponentPlaceholder')
    }

}
